import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { bugEvaluationApi } from '../lib/bug-evaluation-api.js';
import type {
  BugEvaluationReviewInput,
  CauseProjectUpdateInput,
} from '../lib/bug-evaluation-api.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * 現在のi18nロケールから 'ja' | 'en' を返す
 * ja以外はすべてenにフォールバック
 * @graph-connects none
 */
function useApiLocale(): 'ja' | 'en' {
  const { i18n } = useTranslation();
  const lang = i18n.language ?? 'ja';
  return lang.startsWith('ja') ? 'ja' : 'en';
}

/**
 * バグ評価一覧を取得
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ評価一覧をTanStack Query経由で取得
 * @graph-connects /bug-evaluations [calls] バグ評価一覧取得API
 */
export function useBugEvaluations(params?: {
  startDate?: string;
  endDate?: string;
  reviewStatus?: string;
  team?: string;
  /** 期間フィルタの基準日。releasedAt=起因案件リリース日、createdAt=バグ起票日、evaluatedAt=評価日時（デフォルト） */
  dateBasis?: 'evaluatedAt' | 'releasedAt' | 'createdAt';
}) {
  const locale = useApiLocale();
  return useQuery({
    queryKey: [...queryKeys.bugEvaluation.list(params), locale] as const,
    queryFn: () => bugEvaluationApi.getList({ ...params, locale }),
  });
}

/**
 * 自分の担当案件起因バグ評価を取得
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 自分がdev_leaderの案件起因バグ評価を期間フィルタ付きでTanStack Query経由で取得
 * @graph-connects /bug-evaluations/my [calls] 自分のバグ評価一覧取得API
 */
export function useMyBugEvaluations(params?: { startDate?: string; endDate?: string }) {
  const locale = useApiLocale();
  return useQuery({
    queryKey: [...queryKeys.bugEvaluation.all, 'my', locale, params] as const,
    queryFn: () =>
      bugEvaluationApi.getMy({ locale, startDate: params?.startDate, endDate: params?.endDate }),
  });
}

/**
 * バグ評価詳細を取得
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ評価詳細をTanStack Query経由で取得（locale指定で翻訳対応）
 * @graph-connects /bug-evaluations/:issueKey [calls] バグ評価詳細取得API
 */
export function useBugEvaluationDetail(issueKey: string, locale?: 'ja' | 'en') {
  const autoLocale = useApiLocale();
  const effectiveLocale = locale ?? autoLocale;
  return useQuery({
    queryKey: [...queryKeys.bugEvaluation.detail(issueKey), effectiveLocale] as const,
    queryFn: () => bugEvaluationApi.getDetail(issueKey, effectiveLocale),
    enabled: issueKey.length > 0,
  });
}

/**
 * 起因案件の実装担当者の候補を取得する。
 *
 * **確定できる人だけが見る情報**（候補は個人名と GitHub の履歴）なので、編集できるときだけ
 * 引く。API 側も admin / architect / tech_lead に絞っている。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 起因案件の実装担当者の候補をTanStack Query経由で取得（確定権限があるときだけ）
 * @graph-connects /bug-evaluations/:issueKey/implementer-candidates [calls] 実装担当者の候補取得API
 */
export function useImplementerCandidates(issueKey: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.bugEvaluation.implementerCandidates(issueKey),
    queryFn: () => bugEvaluationApi.getImplementerCandidates(issueKey),
    enabled: enabled && issueKey.length > 0,
  });
}

/**
 * 一次判定が見送った課題を取得（確定権限があるときだけ）
 *
 * 見送りは評価本体を持たないので一覧には現れず、Gemini の日次バッチも触らない。
 * **人が見るまで誰も点を付けない**ので、確定する人の目に入る場所に出す必要がある。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 一次判定が見送った課題をTanStack Query経由で取得（確定権限があるときだけ）
 * @graph-connects /bug-evaluations/ai-drafts [calls] 見送られた一次判定の一覧取得API
 */
export function useBugEvaluationAiDeclines(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.bugEvaluation.aiDrafts(),
    queryFn: () => bugEvaluationApi.getAiDrafts(),
    enabled,
  });
}

/**
 * 起因案件の現状と付け替え先候補を取得（確定権限があるときだけ）
 *
 * 置けない種別（統合プロダクト等）が入っていたときの付け替え先は、**そのコードを作った PR が
 * どのチャンクのものか**で決まる。候補にはチャンクごとの PR の件数・リポジトリが付くので、
 * 人は推測ではなく証跡で選べる。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 起因案件の現状と付け替え先候補をTanStack Query経由で取得（確定権限があるときだけ）
 * @graph-connects /bug-evaluations/:issueKey/cause-project/candidates [calls] 起因案件の現状と付け替え先候補の取得API
 */
export function useCauseProjectCandidates(issueKey: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.bugEvaluation.causeProjectCandidates(issueKey),
    queryFn: () => bugEvaluationApi.getCauseProjectCandidates(issueKey),
    enabled: enabled && issueKey.length > 0,
  });
}

/**
 * 起因案件の付け替え
 *
 * 置ける課題種別かの検証は API 側が行う（allowlist を画面に写さない）。拒否されたときは
 * API が返したメッセージをそのまま見せる。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 起因案件をBacklogへ付け替え、候補と評価詳細のキャッシュを無効化する
 * @graph-connects /bug-evaluations/:issueKey/cause-project [calls] 起因案件の付け替えAPI
 */
export function useUpdateCauseProject(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CauseProjectUpdateInput) =>
      bugEvaluationApi.updateCauseProject(issueKey, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bugEvaluation.causeProjectCandidates(issueKey),
      });
      // 評価詳細は BQ を読むため付け替え直後は旧値のままだが、同期後に追いつく。
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bugEvaluation.detail(issueKey),
      });
    },
  });
}

/**
 * バグ評価レビュー更新
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ評価レビューをFirestore経由で更新（EventArcでBQ+Sheet同期）
 * @graph-connects /bug-evaluations/:issueKey [calls] バグ評価レビュー更新API
 */
export function useUpdateBugEvaluation(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BugEvaluationReviewInput) => bugEvaluationApi.updateReview(issueKey, data),
    onSuccess: () => {
      // 詳細と一覧のキャッシュを無効化
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bugEvaluation.detail(issueKey),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.bugEvaluation.all,
      });
    },
  });
}
